import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Label } from "@/components/ui";
import { AlertTriangle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

interface DeleteHelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  helpCenterTitle?: string;
}

const CONFIRMATION_TEXT = "DELETE";

export const DeleteHelpCenterModal: React.FC<DeleteHelpCenterModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  helpCenterTitle,
}) => {
  const { t } = useTranslation();
  const [confirmationText, setConfirmationText] = useState("");
  const isConfirmed = confirmationText === CONFIRMATION_TEXT;

  // Reset confirmation when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationText("");
    }
  }, [isOpen]);

  const handleClose = () => {
    setConfirmationText("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      noHeader
      size="md"
    >
      <div className="px-4 my-4">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-foreground mb-2">
              {t('helpCenter.deleteHelpCenterModal.title')}
            </h3>
            <p className="text-sm text-foreground mb-2">
              <Trans
                i18nKey="helpCenter.deleteHelpCenterModal.confirmMessage"
                values={{ title: helpCenterTitle || '' }}
                components={{ strong: <span className="font-medium" /> }}
              />
            </p>
            <p className="text-sm text-destructive font-medium">
              {t('helpCenter.deleteHelpCenterModal.permanentWarning')}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="confirm-delete" className="text-sm">
            <Trans
              i18nKey="helpCenter.deleteHelpCenterModal.typeToConfirm"
              values={{ text: CONFIRMATION_TEXT }}
              components={{ strong: <span className="font-mono font-semibold" /> }}
            />
          </Label>
          <Input
            id="confirm-delete"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={CONFIRMATION_TEXT}
            className="mt-2 font-mono"
            disabled={isDeleting}
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={isDeleting}
          style={{ backgroundColor: '#efefef' }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={!isConfirmed || isDeleting}
          loading={isDeleting}
        >
          {t('common.delete')}
        </Button>
      </div>
    </Modal>
  );
};

